import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createCn } from '../../../helpers/utils';
import { EmotionAnalysis } from '../../../services/api.service';
import {
  EMOTION_ORDER,
  SpeakerLabelIndex,
  emotionColor,
  resolveLabelMeta,
} from '../utils/voice-analytics.utils';
import { AnalyticsCard } from './meeting-analytics-panel.component';
import classes from '../styles.module.scss';

const cn = createCn('file', classes);

type Props = {
  emotionAnalysis: EmotionAnalysis | null;
  labelIndex: SpeakerLabelIndex;
};

/** Задача B2: карточка «Эмоции» — сводка + распределение по спикерам стек-барами
 *  (neutral серый, positive зелёный, sad синий, angry красный, other жёлтый). */
const EmotionsCard: React.FC<Props> = ({ emotionAnalysis, labelIndex }) => {
  const { t } = useTranslation();

  const rows = useMemo(() => {
    if (!emotionAnalysis?.available) return [];
    return Object.entries(emotionAnalysis.by_speaker || {})
      .map(([label, byEmotion], i) => ({
        meta: resolveLabelMeta(label, labelIndex, i),
        segments: EMOTION_ORDER
          .map(emotion => ({ emotion, fraction: byEmotion[emotion] || 0 }))
          .filter(s => s.fraction > 0),
      }))
      .filter(row => row.segments.length > 0);
  }, [emotionAnalysis, labelIndex]);

  if (!rows.length) return null;

  const summary = emotionAnalysis?.summary;

  return (
    <AnalyticsCard title={t('voice-analytics.emotions-title')} badge="data" defaultOpen>
      {summary?.dominant && (
        <p className={cn('analytics-subtitle')}>
          {t('voice-analytics.emotions-summary', {
            emotion: t(`voice-analytics.emotion-${summary.dominant}`, summary.dominant),
            pct: summary.negative_share_pct,
          })}
        </p>
      )}

      <div className={cn('voice-emotion-legend')}>
        {EMOTION_ORDER.map(emotion => (
          <span key={emotion} className={cn('voice-emotion-legend-item')}>
            <span className={cn('voice-emotion-legend-dot')} style={{ backgroundColor: emotionColor(emotion) }} />
            {t(`voice-analytics.emotion-${emotion}`)}
          </span>
        ))}
      </div>

      <div className={cn('voice-emotion-list')}>
        {rows.map(row => (
          <div key={row.meta.label} className={cn('voice-emotion-row')}>
            <span className={cn('voice-emotion-name')} style={{ color: row.meta.color }} title={row.meta.displayName}>
              {row.meta.shortName}
            </span>
            <div className={cn('voice-emotion-bar')}>
              {row.segments.map(s => (
                <div
                  key={s.emotion}
                  className={cn('voice-emotion-segment')}
                  style={{ width: `${Math.max(s.fraction * 100, 2)}%`, backgroundColor: emotionColor(s.emotion) }}
                  title={`${t(`voice-analytics.emotion-${s.emotion}`)}: ${Math.round(s.fraction * 100)}%`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </AnalyticsCard>
  );
};

export default EmotionsCard;
